import {FormDirtyTracker} from '../../Library/FormDirtyTracker';
import {translate} from '../../Library/Translator';
import {shakeUnsaved} from './UnsavedShake';

const TRACKED_FORM_SELECTOR     = 'form[data-track-dirty]';
const SHAKEABLE_FORM_SELECTOR   = 'form[data-shake-when-dirty]';
const shakeTrackers = new WeakMap();
const initializedForms = new WeakSet();

const formsMatching = (root, selector) => root.matches?.(selector) ? [root] : root.querySelectorAll(selector);

const wrapSubmitButton = (submitButton) => {
    const existingWrapper = submitButton.parentElement;

    if (existingWrapper.classList.contains('dirty-form-actions')) {
        existingWrapper.classList.remove('is-dirty');

        return existingWrapper;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'dirty-form-actions';

    const indicator = document.createElement('span');
    indicator.className = 'unsaved-indicator';
    indicator.textContent = translate('form.unsavedChanges');
    indicator.setAttribute('aria-live', 'polite');

    submitButton.replaceWith(wrapper);
    wrapper.append(submitButton, indicator);

    return wrapper;
};

const initialize = (form) => {
    if (initializedForms.has(form)) {
        return;
    }

    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]')
        ?? Array.from(form.elements).find(element => element.type === 'submit');

    if (!submitButton) {
        return;
    }

    initializedForms.add(form);

    const wrapper = wrapSubmitButton(submitButton);

    form.addEventListener('form:dirty', () => {
        form.classList.add('is-dirty');
        wrapper.classList.add('is-dirty');
    });
    form.addEventListener('form:clean', () => {
        form.classList.remove('is-dirty');
        wrapper.classList.remove('is-dirty');
    });

    const tracker = FormDirtyTracker(form);

    form.addEventListener('form:saved', () => tracker.reset());
    form.addEventListener('form:touch', () => tracker.touch());
};

export const initializeDirtyForms = (root) => {
    formsMatching(root, TRACKED_FORM_SELECTOR).forEach(initialize);
};

export const markAssociatedFormDirty = (target) => {
    const form = target.form;

    if (!form || form.contains(target) || !form.hasAttribute('data-track-dirty')) {
        return;
    }

    if (target.closest('[data-ignore-dirty]')) {
        return;
    }

    form.dispatchEvent(new CustomEvent('form:touch'));
};

export const initializeShakeWhenDirty = (root) => {
    formsMatching(root, SHAKEABLE_FORM_SELECTOR).forEach(form => {
        if (shakeTrackers.has(form)) {
            return;
        }
        shakeTrackers.set(form, FormDirtyTracker(form));
    });
};

export const shakeDirtyFormsOutside = (target) => {
    if (!target.isConnected) {
        return;
    }

    document.querySelectorAll(SHAKEABLE_FORM_SELECTOR).forEach(form => {
        const tracker = shakeTrackers.get(form);
        if (!tracker || form.contains(target) || !tracker.isDirty()) {
            return;
        }

        shakeUnsaved(form);
    });
};
